import type { PathwayCondition, PathwayRange } from "@app/types/dashboard-pathway";

/*
 * A condition on one axis is the labels it carries or a range over its raw value (PT-146). Both live in
 * the same `conditions`, so the board keeps one filter model: one chip row, one reset, one counter.
 */

/** Whether a condition is a range over the raw value rather than a list of labels. */
export function isRange(
  condition: PathwayCondition | null | undefined,
): condition is PathwayRange {
  return condition != null && typeof condition === "object" && !Array.isArray(condition);
}

/** The labels a condition ticks — none for a range. */
export function labelsOf(condition: PathwayCondition | null | undefined): string[] {
  return Array.isArray(condition) ? condition : [];
}

function present(bound: string | number | undefined | null): bound is string | number {
  return bound !== undefined && bound !== null && `${bound}`.trim() !== "";
}

/**
 * The range with its bounds in a fixed order and nothing empty, or `null` when it bounds nothing. The
 * order matters: the query is keyed on its JSON, and `{max, min}` would refetch what `{min, max}` holds.
 */
export function cleanRange(range: PathwayRange | null | undefined): PathwayRange | null {
  if (!range) return null;
  const out: PathwayRange = {};
  if (present(range.min)) out.min = range.min;
  if (present(range.max)) out.max = range.max;
  return out.min === undefined && out.max === undefined ? null : out;
}

/** How many filters a condition counts as: each ticked label, or one range. */
export function conditionWeight(condition: PathwayCondition): number {
  return isRange(condition) ? (cleanRange(condition) ? 1 : 0) : condition.length;
}

/** The conditions as the API reads them: empty ones dropped, ranges in a fixed key order. */
export function activeConditions(
  conditions: Record<string, PathwayCondition>,
): Record<string, PathwayCondition> {
  const out: Record<string, PathwayCondition> = {};
  for (const [key, condition] of Object.entries(conditions)) {
    if (isRange(condition)) {
      const clean = cleanRange(condition);
      if (clean) out[key] = clean;
    } else if (condition.length > 0) {
      out[key] = condition;
    }
  }
  return out;
}

/**
 * What a selection narrows to, whatever order its axes and labels were ticked in: equal keys, same
 * patients. A bound reads as text, so a typed 75 and a received "75" are one bound.
 */
export function conditionsKey(conditions: Record<string, PathwayCondition>): string {
  const active = activeConditions(conditions);
  return JSON.stringify(
    Object.keys(active)
      .sort()
      .map((key) => {
        const condition = active[key];
        return isRange(condition)
          ? [key, "range", condition.min == null ? null : `${condition.min}`, condition.max == null ? null : `${condition.max}`]
          : [key, "labels", ...[...condition].sort()];
      }),
  );
}

/** The board's period as the dashboard holds it: a token, or a window's two days. */
export interface BoardPeriod {
  periode?: string | null;
  periodeStart?: string | null;
  periodeEnd?: string | null;
}

/** What the whole board narrows to — its conditions and its period: equal keys, same selection. */
export function selectionKey(conditions: Record<string, PathwayCondition>, period: BoardPeriod): string {
  return JSON.stringify([
    conditionsKey(conditions),
    period.periode ?? null,
    period.periodeStart ?? null,
    period.periodeEnd ?? null,
  ]);
}

/** A bound as a reader writes it: a day in the locale's order, a month as mm/yyyy, a number in the
 * locale's own decimals (« 1,5 » in French) — a year left ungrouped. */
function boundText(bound: string | number, locale?: string): string {
  const text = `${bound}`.trim();
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (day) {
    return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])).toLocaleDateString(locale);
  }
  const month = /^(\d{4})-(\d{2})$/.exec(text);
  if (month) return `${month[2]}/${month[1]}`;
  const number = Number(text);
  return text !== "" && Number.isFinite(number)
    ? number.toLocaleString(locale, { useGrouping: false, maximumFractionDigits: 2 })
    : text;
}

/** The range as its chip reads it: « ≥ 75 », « ≤ 2025 », « 17/01/2022 → 22/10/2026 ». */
export function rangeText(range: PathwayRange, locale?: string): string {
  const { min, max } = cleanRange(range) ?? {};
  if (min !== undefined && max !== undefined) {
    return `${boundText(min, locale)} → ${boundText(max, locale)}`;
  }
  if (min !== undefined) return `≥ ${boundText(min, locale)}`;
  if (max !== undefined) return `≤ ${boundText(max, locale)}`;
  return "";
}
